import { api } from '@/lib/api'
import { getWebsiteUrl, getPreviewToken } from '@/config/runtime-env'

export interface PageContentRow {
  id: string
  slug: string
  label: string
  draftContent: Record<string, unknown> | null
  publishedContent: Record<string, unknown> | null
  publishedAt: string | null
  updatedAt: string
  updatedBy?: string | null
}

export interface PageStatus {
  slug: string
  label: string
  hasDraft: boolean
  updatedAt: string
  publishedAt: string | null
}

/** The website's own manifest of editable pages — fetched directly from the
 * website (not the backend), so this list and each page's starting content
 * never has to be hand-duplicated into this repo. */
export interface PageManifestEntry {
  slug: string
  label: string
}

export class PageContentService {
  static async listStatus(): Promise<PageStatus[]> {
    return api.get<PageStatus[]>('/admin/page-content/status')
  }

  static async getDraft(slug: string): Promise<Record<string, unknown> | null> {
    return api.get<Record<string, unknown> | null>(`/admin/page-content/${slug}/draft`)
  }

  static async saveDraft(
    slug: string,
    content: Record<string, unknown>,
    label?: string,
  ): Promise<PageContentRow> {
    return api.put<PageContentRow>(`/admin/page-content/${slug}/draft`, { content, label })
  }

  static async publish(slug: string): Promise<PageContentRow> {
    return api.post<PageContentRow>(`/admin/page-content/${slug}/publish`)
  }

  static async discardDraft(slug: string): Promise<PageContentRow> {
    return api.post<PageContentRow>(`/admin/page-content/${slug}/discard-draft`)
  }
}

/** Reads straight from the website's own API (not the nestjs backend) — see
 * website/src/app/api/page-defaults. */
export class WebsiteDefaultsService {
  static async listPages(): Promise<PageManifestEntry[]> {
    const res = await fetch(`${getWebsiteUrl()}/api/page-defaults`)
    if (!res.ok) throw new Error('Could not load the website’s page list')
    const json = await res.json()
    return json.pages
  }

  static async getDefault(slug: string): Promise<{ label: string; content: Record<string, unknown> }> {
    const res = await fetch(`${getWebsiteUrl()}/api/page-defaults/${slug}`)
    if (!res.ok) throw new Error(`Could not load default content for "${slug}"`)
    return res.json()
  }

  /** The gated preview URL for a page, so the live-preview iframe can show
   * unsaved draft edits instead of the published version. The token is a
   * shared secret (same pattern as the site's existing /v1-/v9 design
   * review gate) — present in the website, backend and this portal's env,
   * never a real credential. */
  static previewUrl(slug: string): string {
    const path = slug === 'home' ? '/' : `/${slug}`
    const token = getPreviewToken()
    return `${getWebsiteUrl()}${path}${token ? `?preview=${encodeURIComponent(token)}` : ''}`
  }
}
